import { defaultAxiosInstance } from '../config/axiosConfig';

/**
 * Run History / Run Details for the Agent UI (`/agent-metrics`, permission
 * `dashboard`) — "what have I run, and what happened in that run".
 *
 * Two things make up a run:
 *   - the run ROW (`RunSummary`) — the accounting record, retained indefinitely;
 *   - the run TRACE — prompt/answer/tool calls/steps, retained ~90 days.
 * `has_trace` on the row says whether a trace still exists, so a run can
 * legitimately list with no detail available (ran before the feature shipped,
 * aged out of retention, or tracing switched off server-side).
 *
 * Auth is the caller's x-api-key — attached by the shared axios interceptor.
 */
class RunHistoryService {
  constructor() {
    this.httpClient = defaultAxiosInstance;
  }

  /**
   * List the caller's own runs.
   *
   * `top_level_only` (default true server-side) is the param that matters: a
   * chain writes one row per node and a HIL team one per member leg, so the
   * default gives one entry per thing the user actually triggered — the steps
   * come back as `children` on the detail. Pass false only for a flat debug view.
   *
   * @param {object} params agent_id | chain_id | team_id | run_type | status |
   *   window | start | end | tz | top_level_only | limit | offset
   */
  async listRuns(params = {}) {
    const res = await this.httpClient.get('/agent-metrics/runs', { params });
    return res.data;
  }

  /** Full detail (trace) of one run: input/output, tool calls, steps, children. */
  async getRunDetail(runId) {
    const res = await this.httpClient.get(`/agent-metrics/runs/${encodeURIComponent(runId)}/detail`);
    return res.data;
  }

  /** Metrics only for one run (pre-existing endpoint, unchanged). */
  async getRunMetrics(runId) {
    const res = await this.httpClient.get(`/agent-metrics/runs/${encodeURIComponent(runId)}`);
    return res.data;
  }
}

const runHistoryService = new RunHistoryService();

export default runHistoryService;
